import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import classes from "../homePageNew.module.css";
import { AuthContext } from "../../../context/auth-context";
import useMonitoringHomeData from "../hooks/useMonitoringHomeData";
import { deriveProviderLlms } from "../api/monitoringDerive";
import MonitoringLlmTabs from "./MonitoringLlmTabs";
import MonitoringSelectorBar from "./MonitoringSelectorBar";
import MonitoringSummaryCard from "./MonitoringSummaryCard";
import MonitoringChart from "./MonitoringChart";
import MonitoringInsights from "./MonitoringInsights";
import { fetchMonitoringReportHistory } from "../api/monitoringApi";
import MonitoringReportingStrip from "./MonitoringReportingStrip";
import MonitoringReportsModal from "./MonitoringReportsModal";
import ReportPreviewDrawer from "./ReportPreviewDrawer";
import SectionEmptyState from "./SectionEmptyState";
import { CategoryIcon } from "./icons";

const DIM_ORDER = ["category", "market", "persona", "geography"];

function dimsMatchUpTo(combo, dims, uptoIdx) {
  for (let i = 0; i < uptoIdx; i++) {
    const key = DIM_ORDER[i];
    const wantedId = dims[`${key}Id`];
    if (wantedId != null && combo[key].id !== wantedId) return false;
  }
  return true;
}

// Cascading dropdown options — narrow by every EARLIER-in-order dimension
// already chosen, so an invalid combination (e.g. a persona that never ran
// under the selected category) can't be selected. See
// MONITORING_HOMEPAGE_RESPONSE_FIX_SPEC.md's FE integration notes §3.
function optionsForDim(combinations, dims, dimIdx) {
  const key = DIM_ORDER[dimIdx];
  const filtered = combinations.filter((c) => dimsMatchUpTo(c, dims, dimIdx));
  const map = new Map();
  filtered.forEach((c) => map.set(c[key].id, c[key]));
  return [...map.values()];
}

function dimsFromCombination(combo) {
  return {
    categoryId: combo.category.id,
    marketId: combo.market.id,
    personaId: combo.persona.id,
    geographyId: combo.geography.id,
  };
}

function formatHeaderDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Monitoring tab — fundamentally different shape from Discovery/Brand
 * Audit/Category Analysis, so it doesn't go through ModulePanel: there's
 * no paginated list of past transactions to page through, just one
 * customer-scoped call (getMonitoringHomeData) returning every brand and
 * product's full combination/run tree at once. All the Combination
 * dropdowns, the summary card, the chart, and Key Insights are derived
 * client-side from that single response — see
 * MONITORING_HOMEPAGE_RESPONSE_FIX_SPEC.md for the exact shape.
 *
 * Selection is by PROVIDER (OpenAI, Google, Anthropic, ...), not by exact
 * model version — see monitoringDerive.js's own header comment for why.
 */
export default function MonitoringModule() {
  const { user, selectedCustomerId } = useContext(AuthContext);
  const userId = user?.user_role?.user_id;
  const customerId = selectedCustomerId;
  const navigate = useNavigate();

  const { data, loading, error } = useMonitoringHomeData({ customerId, userId });

  const [runType, setRunType] = useState("brand");
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [dims, setDims] = useState({ categoryId: null, marketId: null, personaId: null, geographyId: null });
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [chartProvider, setChartProvider] = useState(null);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);

  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  const loadReports = useCallback(() => {
    if (!userId) return;
    setReportsLoading(true);
    fetchMonitoringReportHistory({ userId })
      .then(setReports)
      .catch(() => setReports([]))
      .finally(() => setReportsLoading(false));
  }, [userId]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const initializedRef = useRef(false);

  // Reset a fresh entity's selection to its first available combination —
  // shared by the initial defaultSelection load, Type change, and
  // Brand/Product change.
  const applyEntity = (entity) => {
    if (!entity) return;
    setSelectedEntityId(entity.customerEntityId);
    const firstCombo = entity.combinations?.[0];
    if (firstCombo) {
      setDims(dimsFromCombination(firstCombo));
      const providers = deriveProviderLlms(firstCombo);
      setSelectedProviders(providers.map((p) => p.provider));
      setChartProvider(providers[0]?.provider ?? null);
    } else {
      setDims({ categoryId: null, marketId: null, personaId: null, geographyId: null });
      setSelectedProviders([]);
      setChartProvider(null);
    }
  };

  // One-time initialization from the response's own defaultSelection.
  useEffect(() => {
    if (!data || initializedRef.current) return;
    initializedRef.current = true;
    const def = data.defaultSelection;
    const entity = data.entities?.find((e) => e.customerEntityId === def?.entityId) || data.entities?.[0];
    if (!entity) return;
    setRunType(entity.type);
    const defaultCombo = entity.combinations?.find((c) => c.combinationId === def?.combinationId) || entity.combinations?.[0];
    setSelectedEntityId(entity.customerEntityId);
    if (defaultCombo) {
      setDims(dimsFromCombination(defaultCombo));
      const providers = deriveProviderLlms(defaultCombo);
      setSelectedProviders(providers.map((p) => p.provider));
      const matchingProvider = def?.chartLlmId && providers.find((p) => p.customerLlmIds.includes(def.chartLlmId));
      setChartProvider(matchingProvider ? matchingProvider.provider : providers[0]?.provider ?? null);
    }
  }, [data]);

  const hasProducts = !!data?.entities?.some((e) => e.type === "product");
  const entitiesForType = useMemo(() => (data?.entities || []).filter((e) => e.type === runType), [data, runType]);
  const selectedEntity = entitiesForType.find((e) => e.customerEntityId === selectedEntityId) || entitiesForType[0];
  const combinations = selectedEntity?.combinations || [];

  const dimensionOptions = useMemo(
    () => ({
      category: optionsForDim(combinations, dims, 0),
      market: optionsForDim(combinations, dims, 1),
      persona: optionsForDim(combinations, dims, 2),
      geography: optionsForDim(combinations, dims, 3),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [combinations, dims.categoryId, dims.marketId, dims.personaId, dims.geographyId]
  );

  const combo = combinations.find(
    (c) => c.category.id === dims.categoryId && c.market.id === dims.marketId && c.persona.id === dims.personaId && c.geography.id === dims.geographyId
  );

  const providerLlms = useMemo(() => deriveProviderLlms(combo), [combo]);

  const handleDimChange = (dimKey, newId) => {
    setDims((prev) => {
      const next = { ...prev, [`${dimKey}Id`]: newId };
      const startIdx = DIM_ORDER.indexOf(dimKey) + 1;
      for (let i = startIdx; i < DIM_ORDER.length; i++) {
        const key = DIM_ORDER[i];
        const opts = optionsForDim(combinations, next, i);
        const stillValid = opts.some((o) => o.id === next[`${key}Id`]);
        if (!stillValid) next[`${key}Id`] = opts[0]?.id ?? null;
      }
      return next;
    });
  };

  // Combination changed under the same entity — the provider multiselect
  // and chart provider should reset to the new combination's own set, not
  // carry over providers/selections that may not exist for it.
  useEffect(() => {
    if (!combo) return;
    const providers = deriveProviderLlms(combo);
    setSelectedProviders(providers.map((p) => p.provider));
    setChartProvider((prev) => (providers.some((p) => p.provider === prev) ? prev : providers[0]?.provider ?? null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combo?.combinationId]);

  const handleRunTypeChange = (newType) => {
    setRunType(newType);
    const entity = (data?.entities || []).find((e) => e.type === newType);
    applyEntity(entity);
  };

  const handleEntityChange = (newEntityId) => {
    const entity = entitiesForType.find((e) => e.customerEntityId === newEntityId);
    applyEntity(entity);
  };

  const selectedProviderLlms = useMemo(
    () => providerLlms.filter((p) => selectedProviders.includes(p.provider)),
    [providerLlms, selectedProviders]
  );

  const lastRunDate = combo?.runs?.[combo.runs.length - 1]?.runDate;

  // LLM PERFORMANCE table (+ the Visibility/Sentiment tiles and gauge above
  // it) is a snapshot of the CURRENT run — same as the old Home page, it
  // only shows an LLM that actually ran on the latest run date. A
  // provider's `latest` otherwise falls back to its own most recent run
  // even if that's an older date than the combination's latest (e.g. it
  // simply wasn't run this time), which used to leave a stale row/skewed
  // average in the table. The chart's LLM tabs and the multiselect above
  // still list every provider that ever ran this combination — this only
  // narrows what feeds the summary card.
  const currentProviderLlms = useMemo(
    () => selectedProviderLlms.filter((p) => p.latest?.runDate === lastRunDate),
    [selectedProviderLlms, lastRunDate]
  );

  const activeChartProvider = providerLlms.find((p) => p.provider === chartProvider) || providerLlms[0];

  const handleViewFull = () => {
    if (!combo?.fullViewLink) return;
    const { route, params } = combo.fullViewLink;
    const merged = { ...params };
    // Open the full page on the LLM currently picked in the Trend & key
    // insights switcher, not whichever LLM BE defaulted the link to. The
    // raw llmId/llmName live on the run entries (combo.llms only carries
    // the provider grouping), so take them from the latest run that used
    // the active provider's current model. The full page reads llmName
    // (and llmId when present) and then selects every model of that
    // provider — same grouping used here.
    const activeCustomerLlmId = activeChartProvider?.customerLlmId;
    let activeLlm = null;
    for (let i = (combo.runs?.length || 0) - 1; i >= 0 && !activeLlm; i -= 1) {
      activeLlm = combo.runs[i].llms?.find((l) => l.customerLlmId === activeCustomerLlmId) || null;
    }
    if (activeLlm) {
      // BE builds the link's llmName from either the raw name or the
      // display label; use whichever field the original value matches so
      // the replacement is in the same form the full page already expects.
      const runLlms = (combo.runs || []).flatMap((r) => r.llms || []);
      const nameField = !params.llmName || runLlms.some((l) => l.llmName === params.llmName) ? "llmName" : "llmLabel";
      const replacement = activeLlm[nameField] || activeLlm.llmName;
      if (replacement) merged.llmName = replacement;
      if ("llmId" in merged && activeLlm.llmId != null) merged.llmId = activeLlm.llmId;
    }
    navigate(`/luminaire/${route}?${new URLSearchParams(merged).toString()}`);
  };

  return (
    <div className={classes.modSection}>
      <div className={classes.modSectionHead}>
        <div className={classes.modTitleBlock}>
          <span className={classes.modName}>Monitoring</span>
          <span className={classes.weightTag}>recurring · trend over time</span>
        </div>
        {lastRunDate && (
          <span className={classes.metaStat}>
            Last run: <b>{formatHeaderDate(lastRunDate)}</b>
          </span>
        )}
      </div>

      {loading ? (
        <div className={classes.loadingWrap}>
          <CircularProgress size={28} />
        </div>
      ) : error || !data?.entities?.length ? (
        <SectionEmptyState
          icon={CategoryIcon}
          title={error ? "Couldn't load Monitoring data" : "No monitoring data yet"}
          description={error ? "Please try again in a moment." : "Once a brand or product has been monitored, its data will show here."}
        />
      ) : !selectedEntity ? (
        <SectionEmptyState icon={CategoryIcon} title={`No ${runType === "product" ? "products" : "brands"} being monitored`} />
      ) : (
        <>
          <MonitoringSelectorBar
            runType={runType}
            onRunTypeChange={handleRunTypeChange}
            hasProducts={hasProducts}
            entityOptions={entitiesForType.map((e) => ({ id: e.customerEntityId, name: e.name }))}
            selectedEntityId={selectedEntity.customerEntityId}
            onEntityChange={handleEntityChange}
            dimensionOptions={dimensionOptions}
            dims={dims}
            onDimChange={handleDimChange}
            providerLlms={providerLlms}
            selectedProviders={selectedProviders}
            onProvidersChange={setSelectedProviders}
          />

          {!combo ? (
            <SectionEmptyState icon={CategoryIcon} title="No data for this combination" description="Try a different category, market, persona, or geography above." />
          ) : (
            <>
              <div className={classes.monSplit}>
                <div className={classes.monSplitLeft}>
                  <MonitoringSummaryCard selectedProviderLlms={currentProviderLlms} />
                </div>
                <div className={classes.monSplitRight}>
                  <div className={classes.monTrendCard}>
                    <div className={classes.monTrendHead}>
                      <div className={classes.monTrendHeadText}>
                        <span className={classes.monTrendHeadTitle}>Trend &amp; key insights</span>
                        <span className={classes.monTrendHeadHint}>Choose an LLM — the chart and insights below both follow it</span>
                      </div>
                      <MonitoringLlmTabs providerLlms={providerLlms} activeProvider={activeChartProvider?.provider} onChange={setChartProvider} />
                    </div>
                    <MonitoringChart providerLlms={providerLlms} mainBrandName={selectedEntity.name} chartProvider={chartProvider} />
                  <MonitoringInsights
                    providerLlms={providerLlms}
                    chartProvider={chartProvider}
                    isProduct={runType === "product"}
                    resetKey={`${selectedEntity.customerEntityId}|${combo.combinationId ?? JSON.stringify(dims)}`}
                  />
                  </div>
                </div>
              </div>

              <div className={`${classes.detailActionsRow} ${classes.monActionsRow}`}>
                <button className={classes.viewBtn} onClick={handleViewFull} disabled={!combo.fullViewLink}>
                  View Full Monitoring →
                </button>
              </div>

              <MonitoringReportingStrip
                reports={reports}
                loading={reportsLoading}
                onOpenReportsModal={() => setShowReportsModal(true)}
                onViewReport={setPreviewReport}
              />
            </>
          )}
        </>
      )}

      <MonitoringReportsModal
        open={showReportsModal}
        onClose={() => setShowReportsModal(false)}
        rows={reports}
        loading={reportsLoading}
        onRefresh={loadReports}
        onDeleted={(jobId) => setReports((prev) => prev.filter((r) => r.jobId !== jobId))}
        onViewReport={setPreviewReport}
      />
      <ReportPreviewDrawer
        open={!!previewReport}
        onClose={() => setPreviewReport(null)}
        jobId={previewReport?.jobId}
        title={previewReport?.category || "Report"}
      />
    </div>
  );
}
